/**
 * KnowledgeManagementPage
 * 
 * Comprehensive admin page for managing all knowledge base content:
 * - FAQ Management (Support Page FAQs)
 * - FAQ Sections (Support Page sections)
 * - Knowledge Base Articles (Chatbot knowledge)
 * - FAQ Satisfaction Metrics
 * - User Feedback on FAQs
 * 
 * Consolidates all knowledge management features in one place.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { useUrlState } from '../hooks/useUrlState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import { useChatbot } from '../components/chatbot/ChatbotContext';
import { hasPermission, FEATURE_PERMISSIONS, canViewAllData } from '../utils/permissions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  updateDoc,
  setDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { sanitizeString } from '../utils/sanitization';
import { logUserAction, logEntityDeletion } from '../utils/logger';
import Tooltip from '../components/common/Tooltip';
import Toast from '../components/common/Toast';
import LinkifyText from '../components/common/LinkifyText';
import SupportPageFaqEditor from '../components/forms/SupportPageFaqEditor';
import SupportPageSectionEditor from '../components/forms/SupportPageSectionEditor';
import KnowledgeBaseArticleEditor from '../components/forms/KnowledgeBaseArticleEditor';
import KnowledgeBaseList from '../components/chatbot/KnowledgeBaseList';
import PromoteToKBModal from '../components/chatbot/PromoteToKBModal';

// Icons
const PlusIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ThumbsUpIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.633" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5V21M3.75 10.5h2.883" />
  </svg>
);

const ThumbsDownIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className} style={{ transform: 'rotate(180deg)' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.633" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5V21M3.75 10.5h2.883" />
  </svg>
);

const KnowledgeManagementPage = () => {
  const { currentUser } = useAuth();
  const { reloadKnowledgeBase } = useChatbot();
  
  // State
  const [activeTab, setActiveTab] = useUrlState('activeTab', 'faqs');
  const fb = useFilterBar({
    faqSearch: '',
    faqSectionFilter: 'all',
    faqStatusFilter: 'all',
    faqSortBy: 'order',
    sectionSearch: '',
    searchQuery: '',
    kbSourceFilter: 'all',
    kbCategoryFilter: 'all',
    kbStatusFilter: 'all',
    kbSortBy: 'priority',
    feedbackFilter: 'all',
    feedbackSectionFilter: 'all',
    feedbackSearch: '',
  });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  
  // FAQ Management state
  const [faqs, setFaqs] = useState([]);
  const [sections, setSections] = useState([]);
  const [showFaqEditor, setShowFaqEditor] = useState(false);
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  
  // Knowledge Base state
  const [knowledge, setKnowledge] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  // FAQ Feedback state
  const [faqFeedback, setFaqFeedback] = useState([]);
  const [expandedFaqIds, setExpandedFaqIds] = useState(new Set());
  
  // AI Chatbot Feedback state
  const [chatFeedback, setChatFeedback] = useState([]);
  const [promotingFeedback, setPromotingFeedback] = useState(null);
  
  // Inline note editing state for FAQ feedback
  const [notingFeedbackId, setNotingFeedbackId] = useState(null);
  const [feedbackNoteText, setFeedbackNoteText] = useState('');
  const [savingFeedbackNote, setSavingFeedbackNote] = useState(false);
  
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    category: 'general',
    keywords: '',
    steps: '',
    priority: 10
  });

  const canManage = hasPermission(currentUser, FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE) || hasPermission(currentUser, FEATURE_PERMISSIONS.KNOWLEDGE_VIEW) || canViewAllData(currentUser);
  const isReadOnly = !hasPermission(currentUser, FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE);

  // Real-time listeners for FAQs and Sections
  useEffect(() => {
    if (!canManage) return;
    
    setLoading(true);
    
    // FAQs listener
    const faqsQuery = query(
      collection(db, collectionPath('supportPageFaqs')),
      orderBy('order', 'asc')
    );
    
    const unsubscribeFaqs = onSnapshot(faqsQuery, (snapshot) => {
      const faqData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFaqs(faqData);
    }, (err) => {
      console.error('[KnowledgeMgmt] Error listening to FAQs:', err);
    });
    
    // Sections listener
    const sectionsQuery = query(
      collection(db, collectionPath('supportPageSections')),
      orderBy('order', 'asc')
    );
    
    const unsubscribeSections = onSnapshot(sectionsQuery, (snapshot) => {
      const sectionData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSections(sectionData);
    }, (err) => {
      console.error('[KnowledgeMgmt] Error listening to sections:', err);
    });
    
    // Knowledge Base listener
    const knowledgeQuery = query(
      collection(db, collectionPath('chatbotKnowledge')),
      orderBy('priority', 'desc'),
      limit(200)
    );
    
    const unsubscribeKnowledge = onSnapshot(knowledgeQuery, (snapshot) => {
      const knowledgeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setKnowledge(knowledgeData);
      setLoading(false);
    }, (err) => {
      console.error('[KnowledgeMgmt] Error listening to knowledge:', err);
      setLoading(false);
    });
    
    // FAQ Feedback listener
    const feedbackQuery = query(
      collection(db, collectionPath('faqFeedback')),
      orderBy('timestamp', 'desc'),
      limit(500)
    );
    
    const unsubscribeFeedback = onSnapshot(feedbackQuery, (snapshot) => {
      const feedbackData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));
      setFaqFeedback(feedbackData);
    }, (err) => {
      console.error('[KnowledgeMgmt] Error listening to FAQ feedback:', err);
    });
    
    // Chat Feedback listener (AI chatbot responses)
    const chatFeedbackQuery = query(
      collection(db, collectionPath('chatFeedback')),
      orderBy('timestamp', 'desc'),
      limit(500)
    );
    
    const unsubscribeChatFeedback = onSnapshot(chatFeedbackQuery, (snapshot) => {
      const chatFeedbackData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));
      setChatFeedback(chatFeedbackData);
    }, (err) => {
      console.error('[KnowledgeMgmt] Error listening to chat feedback:', err);
    });
    
    return () => {
      unsubscribeFaqs();
      unsubscribeSections();
      unsubscribeKnowledge();
      unsubscribeFeedback();
      unsubscribeChatFeedback();
    };
  }, [canManage]);

  // Filter Knowledge Base
  const filteredKnowledge = useMemo(() => {
    let filtered = knowledge.filter(entry => {
      // Source filter
      if (fb.applied.kbSourceFilter !== 'all' && entry.source !== fb.applied.kbSourceFilter) return false;
      
      // Category filter
      if (fb.applied.kbCategoryFilter !== 'all' && entry.category !== fb.applied.kbCategoryFilter) return false;
      
      // Status filter
      if (fb.applied.kbStatusFilter !== 'all') {
        const entryStatus = entry.status || (entry.isActive !== false ? 'published' : 'archived');
        if (entryStatus !== fb.applied.kbStatusFilter) return false;
      }
      
      // Search filter
      if (fb.applied.searchQuery) {
        const query = fb.applied.searchQuery.toLowerCase();
        return (
          entry.title?.toLowerCase().includes(query) ||
          entry.content?.toLowerCase().includes(query) ||
          entry.keywords?.some(k => k.toLowerCase().includes(query))
        );
      }
      
      return true;
    });
    
    // Sort
    filtered.sort((a, b) => {
      switch (fb.applied.kbSortBy) {
        case 'priority':
          return (b.priority || 0) - (a.priority || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'createdAt':
          return (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0);
        case 'updatedAt':
          return (b.updatedAt?.toDate?.() || 0) - (a.updatedAt?.toDate?.() || 0);
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [knowledge, fb.applied.kbSourceFilter, fb.applied.kbCategoryFilter, fb.applied.kbStatusFilter, fb.applied.kbSortBy, fb.applied.searchQuery]);

  // Get unique categories from knowledge base
  const categories = useMemo(() => {
    const cats = new Set(knowledge.map(k => k.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [knowledge]);

  // Lifecycle metrics for both FAQs and KB articles
  const lifecycleMetrics = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const allItems = [
      ...knowledge.map(k => ({ ...k, itemType: 'kb' })),
      ...faqs.map(f => ({ ...f, itemType: 'faq', title: f.question }))
    ];
    
    const drafts = allItems.filter(i => i.status === 'draft');
    const needsReview = allItems.filter(i => i.status === 'needs-review');
    const reviewOverdue = allItems.filter(i => i.reviewDate && i.reviewDate <= today && i.status !== 'archived');
    const expiringSoon = allItems.filter(i => {
      if (!i.expiresAt || i.status === 'archived') return false;
      const sevenDays = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
      return i.expiresAt <= sevenDays;
    });
    const expired = allItems.filter(i => i.expiresAt && i.expiresAt <= today && i.status !== 'archived');
    const archived = allItems.filter(i => (i.status === 'archived') || (!i.status && i.isActive === false));
    const missingReviewDate = allItems.filter(i => !i.reviewDate && i.status !== 'archived' && i.status !== 'draft');
    
    return { allItems, drafts, needsReview, reviewOverdue, expiringSoon, expired, archived, missingReviewDate };
  }, [knowledge, faqs]);

  // Calculate FAQ satisfaction metrics with per-FAQ feedback entries
  const faqMetrics = useMemo(() => {
    const metrics = {};
    const faqById = new Map(faqs.map(f => [f.id, f]));
    
    faqs.forEach(faq => {
      const faqFeedbackItems = faqFeedback
        .filter(fb => fb.faqId === faq.id)
        .map(fb => ({
          ...fb,
          userName: fb.displayName || fb.userEmail || 'Unknown',
        }));
      const helpful = faqFeedbackItems.filter(fb => fb.isHelpful === true).length;
      const notHelpful = faqFeedbackItems.filter(fb => fb.isHelpful === false).length;
      const unreviewed = faqFeedbackItems.filter(fb => !fb.isHelpful && !fb.reviewed).length;
      const total = helpful + notHelpful;
      
      metrics[faq.id] = {
        helpful,
        notHelpful,
        unreviewed,
        total,
        satisfactionRate: total > 0 ? (helpful / total * 100).toFixed(1) : null,
        feedbackItems: faqFeedbackItems
      };
    });
    
    return metrics;
  }, [faqs, faqFeedback]);

  // Filter FAQs
  const filteredFaqs = useMemo(() => {
    let filtered = faqs.filter(faq => {
      // Section filter
      if (fb.applied.faqSectionFilter !== 'all' && faq.sectionId !== fb.applied.faqSectionFilter) return false;
      
      // Status filter
      if (fb.applied.faqStatusFilter !== 'all') {
        const faqStatus = faq.status || (faq.isActive === false ? 'archived' : 'published');
        if (faqStatus !== fb.applied.faqStatusFilter) return false;
      }
      
      // Search filter
      if (fb.applied.faqSearch) {
        const query = fb.applied.faqSearch.toLowerCase();
        return (
          faq.question?.toLowerCase().includes(query) ||
          faq.answer?.toLowerCase().includes(query) ||
          faq.sectionTitle?.toLowerCase().includes(query) ||
          faq.keywords?.some(k => k.toLowerCase().includes(query))
        );
      }
      
      return true;
    });
    
    // Sort
    filtered.sort((a, b) => {
      switch (fb.applied.faqSortBy) {
        case 'order':
          return (a.order || 0) - (b.order || 0);
        case 'question':
          return (a.question || '').localeCompare(b.question || '');
        case 'section':
          return (a.sectionTitle || '').localeCompare(b.sectionTitle || '');
        case 'satisfaction':
          const metricsA = faqMetrics[a.id] || {};
          const metricsB = faqMetrics[b.id] || {};
          return (parseFloat(metricsB.satisfactionRate) || 0) - (parseFloat(metricsA.satisfactionRate) || 0);
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [faqs, fb.applied.faqSectionFilter, fb.applied.faqStatusFilter, fb.applied.faqSortBy, fb.applied.faqSearch, faqMetrics]);

  // FAQ feedback stats for stat cards
  const faqChatStats = useMemo(() => {
    const total = faqFeedback.length;
    const helpful = faqFeedback.filter(f => f.isHelpful).length;
    const notHelpful = faqFeedback.filter(f => !f.isHelpful).length;
    const unreviewed = faqFeedback.filter(f => !f.isHelpful && !f.reviewed).length;
    const satisfactionRate = total > 0 ? ((helpful / total) * 100).toFixed(1) : 0;
    return { total, helpful, notHelpful, unreviewed, satisfactionRate };
  }, [faqFeedback]);

  // Toggle expand/collapse for FAQ feedback detail
  const toggleFaqExpanded = (faqId) => {
    setExpandedFaqIds(prev => {
      const next = new Set(prev);
      if (next.has(faqId)) next.delete(faqId);
      else next.add(faqId);
      return next;
    });
  };

  // Save inline admin note on FAQ feedback
  const handleSaveFeedbackNote = async (fbId) => {
    if (!feedbackNoteText.trim()) return;
    setSavingFeedbackNote(true);
    try {
      await addNoteFeedback(fbId, feedbackNoteText.trim());
      setNotingFeedbackId(null);
      setFeedbackNoteText('');
    } catch (err) { /* handled by parent */ }
    finally { setSavingFeedbackNote(false); }
  };

  // Mark an item for review (available to any user)
  const handleMarkForReview = async (item) => {
    try {
      const collName = item.itemType === 'faq' ? 'supportPageFaqs' : 'chatbotKnowledge';
      // Use the original item id (without itemType added by lifecycle metrics)
      await updateDoc(doc(db, collectionPath(collName), item.id), {
        status: 'needs-review',
        updatedAt: serverTimestamp()
      });
      await logUserAction({
        action: item.itemType === 'faq' ? 'FAQ Flagged for Review' : 'Chatbot Knowledge Flagged for Review',
        details: `Flagged for review: "${item.title || item.question || item.id}"`,
        entityType: item.itemType === 'faq' ? 'Faq' : 'ChatbotKnowledge',
        entityId: item.id
      });
      setToast({ message: `"${item.title || item.question}" flagged for review`, type: 'success' });
    } catch (err) {
      console.error('Error marking for review:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Delete FAQ entry
  const handleDeleteFaq = async (faq) => {
    try {
      await deleteDoc(doc(db, collectionPath('supportPageFaqs'), faq.id));
      await logEntityDeletion({
        user: currentUser,
        entityType: 'Faq',
        entityId: faq.id,
        entityName: faq.question || faq.id
      });
      setToast({ message: 'FAQ deleted successfully', type: 'success' });
      reloadKnowledgeBase?.(); // Sync with chatbot knowledge
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Delete knowledge entry
  const handleDeleteKnowledge = async (id) => {
    try {
      const existing = knowledge.find(k => k.id === id);
      await deleteDoc(doc(db, collectionPath('chatbotKnowledge'), id));
      await logEntityDeletion({
        user: currentUser,
        entityType: 'ChatbotKnowledge',
        entityId: id,
        entityName: existing?.title || id
      });
      setToast({ message: 'Knowledge entry deleted successfully', type: 'success' });
      setConfirmDelete(null);
      reloadKnowledgeBase?.();
    } catch (err) {
      console.error('Error deleting knowledge:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Toggle knowledge entry active status
  const toggleKnowledgeActive = async (id, currentActive) => {
    try {
      await updateDoc(doc(db, collectionPath('chatbotKnowledge'), id), {
        isActive: !currentActive,
        updatedAt: serverTimestamp()
      });
      await logUserAction({
        action: !currentActive ? 'Chatbot Knowledge Enabled' : 'Chatbot Knowledge Disabled',
        details: `Knowledge entry ${!currentActive ? 'enabled' : 'disabled'}`,
        entityType: 'ChatbotKnowledge',
        entityId: id
      });
      setToast({ message: `Article ${!currentActive ? 'enabled' : 'disabled'}`, type: 'success' });
      reloadKnowledgeBase?.();
    } catch (err) {
      console.error('Error toggling knowledge active:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Promote AI response to Knowledge Base
  const promoteToKnowledgeBase = async (feedback, title, category, keywords) => {
    try {
      // Generate document ID following naming conventions
      const timestamp = new Date().toISOString().replace(/[:.]/g, '').substring(0, 15);
      const baseTitle = title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').substring(0, 30);
      const docId = `kb_ai_${baseTitle}_${timestamp}`;
      
      // Create knowledge base entry
      await setDoc(doc(db, collectionPath('chatbotKnowledge'), docId), {
        title: sanitizeString(title, 200),
        content: feedback.assistantResponse,
        category: category || 'general',
        keywords: keywords.split(',').map(k => sanitizeString(k.trim(), 50)).filter(Boolean),
        steps: [],
        source: 'ai-feedback',
        priority: 15,
        isActive: true,
        promotedFromFeedbackId: feedback.id,
        originalQuestion: feedback.userQuestion,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Mark feedback as promoted
      await updateDoc(doc(db, collectionPath('chatFeedback'), feedback.id), {
        promotedToKB: true,
        promotedAt: serverTimestamp(),
        promotedBy: currentUser.uid,
        promotedKBId: docId
      });

      await logUserAction({
        action: 'Chatbot Knowledge Promoted',
        details: `Promoted AI response to KB: "${title}" (category: ${category || 'general'})`,
        entityType: 'ChatbotKnowledge',
        entityId: docId
      });

      setToast({ message: 'AI response promoted to Knowledge Base!', type: 'success' });
      setPromotingFeedback(null);
      reloadKnowledgeBase?.();
    } catch (err) {
      console.error('Error promoting to KB:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Dismiss feedback (mark as reviewed without promoting)
  const dismissFeedback = async (feedbackId) => {
    try {
      await updateDoc(doc(db, collectionPath('faqFeedback'), feedbackId), {
        reviewed: true,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid
      });
      await logUserAction({
        action: 'FAQ Feedback Reviewed',
        details: 'Marked FAQ feedback as reviewed (dismissed)',
        entityType: 'Faq',
        entityId: feedbackId
      });
      setToast({ message: 'Feedback marked as reviewed', type: 'success' });
    } catch (err) {
      console.error('Error dismissing feedback:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
    }
  };

  // Add admin notes to feedback (marks as reviewed too)
  const addNoteFeedback = async (feedbackId, notes) => {
    try {
      await updateDoc(doc(db, collectionPath('faqFeedback'), feedbackId), {
        adminNotes: notes,
        reviewed: true,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid
      });
      await logUserAction({
        action: 'FAQ Feedback Note Added',
        details: 'Added admin note to FAQ feedback and marked reviewed',
        entityType: 'Faq',
        entityId: feedbackId
      });
      setToast({ message: 'Notes saved and feedback marked as reviewed', type: 'success' });
    } catch (err) {
      console.error('Error adding note to feedback:', err);
      setToast({ message: `Error: ${err.message}`, type: 'error' });
      throw err;
    }
  };

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access Knowledge Management.</p>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Management</h1>
          <p className="text-gray-600">
            Manage FAQs, support content, and knowledge base articles for the CRM and chatbot.
          </p>
        </div>

        {/* FAQ Chatbot Feedback Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div 
            onClick={() => { setActiveTab('faq-feedback'); fb.setDraft('feedbackFilter', 'all'); setTimeout(() => fb.apply(), 0); }}
            className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500 cursor-pointer hover:bg-blue-50 transition-colors"
          >
            <div className="text-sm text-gray-600">FAQ Feedback</div>
            <div className="text-2xl font-bold">{faqChatStats.total}</div>
          </div>
          <div 
            onClick={() => { setActiveTab('faq-feedback'); fb.setDraft('feedbackFilter', 'helpful'); setTimeout(() => fb.apply(), 0); }}
            className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500 cursor-pointer hover:bg-green-50 transition-colors"
          >
            <div className="text-sm text-gray-600">FAQ Helpful</div>
            <div className="text-2xl font-bold text-green-600">{faqChatStats.helpful}</div>
          </div>
          <div 
            onClick={() => { setActiveTab('faq-feedback'); fb.setDraft('feedbackFilter', 'not-helpful'); setTimeout(() => fb.apply(), 0); }}
            className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500 cursor-pointer hover:bg-red-50 transition-colors"
          >
            <div className="text-sm text-gray-600">FAQ Not Helpful</div>
            <div className="text-2xl font-bold text-red-600">{faqChatStats.notHelpful}</div>
          </div>
          <div 
            onClick={() => { setActiveTab('faq-feedback'); fb.setDraft('feedbackFilter', 'unreviewed'); setTimeout(() => fb.apply(), 0); }}
            className="bg-white p-4 rounded-lg shadow border-l-4 border-amber-500 cursor-pointer hover:bg-amber-50 transition-colors"
          >
            <div className="text-sm text-gray-600">Unreviewed</div>
            <div className="text-2xl font-bold text-amber-600">{faqChatStats.unreviewed}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
            <div className="text-sm text-gray-600">FAQ Satisfaction</div>
            <div className="text-2xl font-bold text-purple-600">{faqChatStats.satisfactionRate}%</div>
            <div className="text-xs text-gray-400">{faqChatStats.helpful} of {faqChatStats.total} helpful</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('faqs')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'faqs'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              ❓ FAQ Management ({faqs.length})
            </button>
            <button
              onClick={() => setActiveTab('sections')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'sections'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📁 FAQ Sections ({sections.length})
            </button>
            <button
              onClick={() => setActiveTab('faq-feedback')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'faq-feedback'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              💬 FAQ Feedback ({faqFeedback.length})
            </button>
            <button
              onClick={() => setActiveTab('lifecycle')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'lifecycle'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              🔄 Lifecycle {(lifecycleMetrics.drafts.length + lifecycleMetrics.needsReview.length + lifecycleMetrics.reviewOverdue.length + lifecycleMetrics.expired.length + lifecycleMetrics.missingReviewDate.length) > 0 && (
                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">
                  {lifecycleMetrics.drafts.length + lifecycleMetrics.needsReview.length + lifecycleMetrics.reviewOverdue.length + lifecycleMetrics.expired.length + lifecycleMetrics.missingReviewDate.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('knowledge')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'knowledge'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              📚 AI Knowledge Base ({knowledge.length})
            </button>
            
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {/* FAQ Management Tab */}
            {activeTab === 'faqs' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">FAQ Management</h3>
                    <p className="text-sm text-gray-600">Manage support page FAQs across all sections</p>
                  </div>
                  {!isReadOnly && (
                  <button
                    onClick={() => {
                      setEditingFaq(null);
                      setShowFaqEditor(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add FAQ
                  </button>
                  )}
                </div>

                {/* Filters Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <select
                    value={fb.draft.faqSectionFilter}
                    onChange={(e) => fb.setDraft('faqSectionFilter', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Sections</option>
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>{section.icon} {section.title}</option>
                    ))}
                  </select>
                  
                  <select
                    value={fb.draft.faqStatusFilter}
                    onChange={(e) => fb.setDraft('faqStatusFilter', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="needs-review">Needs Review</option>
                    <option value="archived">Archived</option>
                  </select>
                  
                  <select
                    value={fb.draft.faqSortBy}
                    onChange={(e) => fb.setDraft('faqSortBy', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="order">Sort by Order</option>
                    <option value="question">Sort by Question</option>
                    <option value="section">Sort by Section</option>
                    <option value="satisfaction">Sort by Satisfaction</option>
                  </select>
                  
                  <div className="flex items-center justify-end text-sm text-gray-600">
                    <span>Showing {filteredFaqs.length} of {faqs.length} FAQs</span>
                  </div>
                </div>

                {/* Search Row */}
                <div className="flex gap-3 items-center">
                  <input
                    type="search"
                    value={fb.draft.faqSearch}
                    onChange={(e) => fb.setDraft('faqSearch', e.target.value)}
                    onKeyDown={fb.applyOnEnter}
                    placeholder="Search FAQs by question, answer, section, or keywords..."
                    className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${fb.draft.faqSearch ? 'border-yellow-400 ring-1 ring-yellow-400' : 'border-gray-300'}`}
                  />
                  <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {filteredFaqs.map(faq => {
                      const metrics = faqMetrics[faq.id] || {};
                      return (
                        <div key={faq.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-lg">{faq.sectionIcon}</span>
                                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                  {faq.sectionTitle}
                                </span>
                                {(() => {
                                  const s = faq.status || (faq.isActive === false ? 'archived' : 'published');
                                  const cfg = {
                                    published: { label: 'Published', cls: 'bg-green-100 text-green-700' },
                                    draft: { label: 'Draft', cls: 'bg-gray-200 text-gray-600' },
                                    'needs-review': { label: 'Needs Review', cls: 'bg-amber-100 text-amber-700' },
                                    archived: { label: 'Archived', cls: 'bg-red-100 text-red-700' },
                                  };
                                  const { label, cls } = cfg[s] || { label: s, cls: 'bg-gray-100 text-gray-600' };
                                  return (
                                    <span className={`text-xs px-2 py-1 rounded ${cls}`}>{label}</span>
                                  );
                                })()}
                                {faq.reviewDate && faq.reviewDate <= new Date().toISOString().split('T')[0] && faq.status !== 'archived' && (
                                  <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded font-medium">
                                    Review Due
                                  </span>
                                )}
                                <span className="text-xs text-gray-500">Order: {faq.order}</span>
                                {metrics.total > 0 && (
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    metrics.satisfactionRate >= 75 ? 'bg-green-100 text-green-700' :
                                    metrics.satisfactionRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {metrics.satisfactionRate}% satisfaction ({metrics.total} votes)
                                  </span>
                                )}
                              </div>
                              <h4 className="font-semibold text-gray-900 mb-1 break-words">{faq.question}</h4>
                              <p className="text-sm text-gray-600 line-clamp-2 break-words">{faq.answer}</p>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                                {faq.updatedAt && (
                                  <span>Updated: {faq.updatedAt?.toDate?.()?.toLocaleDateString() || '—'}</span>
                                )}
                                {faq.reviewDate && (
                                  <span className={faq.reviewDate <= new Date().toISOString().split('T')[0] ? 'text-orange-600 font-medium' : ''}>
                                    Review: {faq.reviewDate}
                                  </span>
                                )}
                                {!faq.reviewDate && faq.status !== 'draft' && faq.status !== 'archived' && (
                                  <span className="text-indigo-500 italic">No review date</span>
                                )}
                                {faq.steps && faq.steps.length > 0 && (
                                  <span>{faq.steps.length} steps</span>
                                )}
                              </div>
                              {faq.keywords && faq.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {faq.keywords.slice(0, 5).map((kw, idx) => (
                                    <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                      {kw}
                                    </span>
                                  ))}
                                  {faq.keywords.length > 5 && (
                                    <span className="text-xs text-gray-500">+{faq.keywords.length - 5} more</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              {/* Flag for Review - available to any user */}
                              {faq.status !== 'needs-review' && faq.status !== 'archived' && faq.status !== 'draft' && (
                                <Tooltip text="Flag this FAQ for admin review">
                                  <button
                                    onClick={() => handleMarkForReview({ ...faq, itemType: 'faq', title: faq.question })}
                                    className="px-3 py-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                                  >
                                    Flag Review
                                  </button>
                                </Tooltip>
                              )}
                              {!isReadOnly && (
                              <>
                              <Tooltip text="Edit FAQ">
                                <button
                                  onClick={() => {
                                    setEditingFaq(faq);
                                    setShowFaqEditor(true);
                                  }}
                                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                  Edit
                                </button>
                              </Tooltip>
                              <Tooltip text="Delete FAQ">
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete this FAQ?\n\n"${faq.question}"`)) {
                                      handleDeleteFaq(faq);
                                    }
                                  }}
                                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                >
                                  Delete
                                </button>
                              </Tooltip>
                              </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>

                {faqs.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No FAQs found. Click "Add FAQ" to create your first FAQ.
                  </div>
                )}
              </div>
            )}

            {/* Section Management Tab */}
            {activeTab === 'sections' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">FAQ Sections</h3>
                    <p className="text-sm text-gray-600">Manage FAQ sections and their organization</p>
                  </div>
                  {!isReadOnly && (
                  <button
                    onClick={() => {
                      setEditingSection(null);
                      setShowSectionEditor(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add Section
                  </button>
                  )}
                </div>

                <div className="flex justify-between items-center gap-3">
                  <input
                    type="search"
                    value={fb.draft.sectionSearch}
                    onChange={(e) => fb.setDraft('sectionSearch', e.target.value)}
                    onKeyDown={fb.applyOnEnter}
                    placeholder="Search sections..."
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-full sm:w-96"
                  />
                  <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
                  <span className="text-sm text-gray-600 ml-4 whitespace-nowrap">
                    {sections.filter(s =>
                      fb.applied.sectionSearch === '' ||
                      s.title?.toLowerCase().includes(fb.applied.sectionSearch.toLowerCase()) ||
                      s.description?.toLowerCase().includes(fb.applied.sectionSearch.toLowerCase())
                    ).length} of {sections.length} sections
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sections
                    .filter(s =>
                      fb.applied.sectionSearch === '' ||
                      s.title?.toLowerCase().includes(fb.applied.sectionSearch.toLowerCase()) ||
                      s.description?.toLowerCase().includes(fb.applied.sectionSearch.toLowerCase())
                    )
                    .map(section => {
                      const faqCount = faqs.filter(f => f.sectionId === section.id).length;
                      return (
                        <div key={section.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-2xl flex-shrink-0">{section.icon}</span>
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900 break-words">{section.title}</h4>
                                <p className="text-xs text-gray-500">Order: {section.order}</p>
                              </div>
                            </div>
                            {!isReadOnly && (
                            <button
                              onClick={() => {
                                setEditingSection(section);
                                setShowSectionEditor(true);
                              }}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex-shrink-0"
                            >
                              Edit
                            </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2 break-words">{section.description}</p>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">{faqCount} FAQs</span>
                            <span className={`px-2 py-0.5 rounded ${
                              !section.requiredPermission ? 'bg-green-100 text-green-700' :
                              section.requiredPermission === 'logged-in' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {!section.requiredPermission ? 'Public' :
                               section.requiredPermission === 'logged-in' ? 'Logged In' :
                               'Admins Only'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>

                {sections.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No sections found. Click "Add Section" to create your first FAQ section.
                  </div>
                )}
              </div>
            )}

            {/* Knowledge Base Tab */}
            {activeTab === 'knowledge' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Knowledge Base</h3>
                    <p className="text-sm text-gray-600">Manage chatbot knowledge base articles</p>
                  </div>
                  {!isReadOnly && (
                    <button
                      onClick={() => {
                        setEditingEntry(null);
                        setShowEditModal(true);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Create Article
                    </button>
                  )}
                </div>

                <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
                <KnowledgeBaseList
                  knowledge={knowledge}
                  filteredKnowledge={filteredKnowledge}
                  categories={categories}
                  searchQuery={fb.draft.searchQuery}
                  setSearchQuery={(v) => fb.setDraft('searchQuery', v)}
                  sourceFilter={fb.draft.kbSourceFilter}
                  setSourceFilter={(v) => fb.setDraft('kbSourceFilter', v)}
                  categoryFilter={fb.draft.kbCategoryFilter}
                  setCategoryFilter={(v) => fb.setDraft('kbCategoryFilter', v)}
                  statusFilter={fb.draft.kbStatusFilter}
                  setStatusFilter={(v) => fb.setDraft('kbStatusFilter', v)}
                  sortBy={fb.draft.kbSortBy}
                  setSortBy={(v) => fb.setDraft('kbSortBy', v)}
                  onEdit={isReadOnly ? null : (entry) => {
                    setEditingEntry(entry);
                    setShowEditModal(true);
                  }}
                  onDelete={isReadOnly ? null : (entry) => {
                    if (window.confirm('Are you sure you want to delete this knowledge article?')) {
                      handleDeleteKnowledge(entry.id);
                    }
                  }}
                  onToggleActive={isReadOnly ? null : toggleKnowledgeActive}
                  onMarkForReview={(entry) => handleMarkForReview({ ...entry, itemType: 'kb' })}
                />
              </div>
            )}

            {/* FAQ Feedback Tab — Merged satisfaction + individual feedback */}
            {activeTab === 'faq-feedback' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">FAQ Feedback &amp; Satisfaction</h3>
                  <p className="text-sm text-gray-600">Review user feedback per FAQ, satisfaction rates, and admin actions</p>
                </div>

                {/* Summary Cards — clickable */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div 
                    onClick={() => { fb.setDraft('feedbackFilter', 'all'); setTimeout(() => fb.apply(), 0); }}
                    className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                      fb.applied.feedbackFilter === 'all' ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-200'
                    }`}
                  >
                    <div className="text-sm text-gray-600 mb-1">Total Feedback</div>
                    <div className="text-2xl font-bold text-gray-900">{faqChatStats.total}</div>
                  </div>
                  <div 
                    onClick={() => { fb.setDraft('feedbackFilter', 'helpful'); setTimeout(() => fb.apply(), 0); }}
                    className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                      fb.applied.feedbackFilter === 'helpful' ? 'border-green-500 ring-2 ring-green-200' : 'border-green-200'
                    }`}
                  >
                    <div className="text-sm text-gray-600 mb-1">Helpful</div>
                    <div className="text-2xl font-bold text-green-600">{faqChatStats.helpful}</div>
                  </div>
                  <div 
                    onClick={() => { fb.setDraft('feedbackFilter', 'not-helpful'); setTimeout(() => fb.apply(), 0); }}
                    className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                      fb.applied.feedbackFilter === 'not-helpful' ? 'border-red-500 ring-2 ring-red-200' : 'border-red-200'
                    }`}
                  >
                    <div className="text-sm text-gray-600 mb-1">Not Helpful</div>
                    <div className="text-2xl font-bold text-red-600">{faqChatStats.notHelpful}</div>
                  </div>
                  <div 
                    onClick={() => { fb.setDraft('feedbackFilter', 'unreviewed'); setTimeout(() => fb.apply(), 0); }}
                    className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                      fb.applied.feedbackFilter === 'unreviewed' ? 'border-amber-500 ring-2 ring-amber-200 bg-amber-100' : 'border-amber-200 bg-amber-50'
                    }`}
                  >
                    <div className="text-sm text-amber-700 mb-1">Unreviewed</div>
                    <div className="text-2xl font-bold text-amber-600">{faqChatStats.unreviewed}</div>
                  </div>
                  <div className="bg-white border border-purple-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Overall Satisfaction</div>
                    <div className="text-2xl font-bold text-purple-600">{faqChatStats.satisfactionRate}%</div>
                    <div className="text-xs text-gray-400">{faqChatStats.helpful} of {faqChatStats.total} helpful</div>
                  </div>
                </div>

                {/* Filter + Search */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <select
                    value={fb.draft.feedbackFilter}
                    onChange={(e) => fb.setDraft('feedbackFilter', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Feedback</option>
                    <option value="helpful">Helpful Only</option>
                    <option value="not-helpful">Not Helpful Only</option>
                    <option value="unreviewed">Unreviewed</option>
                  </select>
                  <select
                    value={fb.draft.feedbackSectionFilter}
                    onChange={(e) => fb.setDraft('feedbackSectionFilter', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Sections</option>
                    {sections.map(section => (
                      <option key={section.id} value={section.id}>{section.icon} {section.title}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Search FAQs by question, answer, or section..."
                    value={fb.draft.feedbackSearch}
                    onChange={(e) => fb.setDraft('feedbackSearch', e.target.value)}
                    onKeyDown={fb.applyOnEnter}
                    className={`flex-1 w-full sm:w-auto px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                      fb.draft.feedbackSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                    }`}
                  />
                  <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
                  <span className="text-sm text-gray-600">
                    {faqs.filter(faq => {
                      const metrics = faqMetrics[faq.id];
                      if (!metrics || metrics.total === 0) return false;
                      if (fb.applied.feedbackFilter === 'helpful') return metrics.helpful > 0;
                      if (fb.applied.feedbackFilter === 'not-helpful') return metrics.notHelpful > 0;
                      if (fb.applied.feedbackFilter === 'unreviewed') return metrics.unreviewed > 0;
                      return true;
                    }).filter(faq => {
                      if (fb.applied.feedbackSectionFilter !== 'all' && faq.sectionId !== fb.applied.feedbackSectionFilter) return false;
                      return true;
                    }).filter(faq => {
                      if (!fb.applied.feedbackSearch) return true;
                      const q = fb.applied.feedbackSearch.toLowerCase();
                      return (faq.question || '').toLowerCase().includes(q)
                        || (faq.answer || '').toLowerCase().includes(q)
                        || (faq.sectionTitle || '').toLowerCase().includes(q);
                    }).length} FAQs with feedback
                  </span>
                </div>

                {/* Per-FAQ cards with expandable detail */}
                <div className="space-y-3">
                  {faqs
                    .filter(faq => {
                      const metrics = faqMetrics[faq.id];
                      if (!metrics || metrics.total === 0) return false;
                      if (fb.applied.feedbackFilter === 'helpful') return metrics.helpful > 0;
                      if (fb.applied.feedbackFilter === 'not-helpful') return metrics.notHelpful > 0;
                      if (fb.applied.feedbackFilter === 'unreviewed') return metrics.unreviewed > 0;
                      return true;
                    })
                    .filter(faq => {
                      if (fb.applied.feedbackSectionFilter !== 'all' && faq.sectionId !== fb.applied.feedbackSectionFilter) return false;
                      return true;
                    })
                    .filter(faq => {
                      if (!fb.applied.feedbackSearch) return true;
                      const q = fb.applied.feedbackSearch.toLowerCase();
                      return (faq.question || '').toLowerCase().includes(q)
                        || (faq.answer || '').toLowerCase().includes(q)
                        || (faq.sectionTitle || '').toLowerCase().includes(q);
                    })
                    .sort((a, b) => (faqMetrics[b.id]?.total || 0) - (faqMetrics[a.id]?.total || 0))
                    .map(faq => {
                      const metrics = faqMetrics[faq.id];
                      const isExpanded = expandedFaqIds.has(faq.id);
                      // Filter individual feedback items based on active filter
                      const visibleItems = (metrics.feedbackItems || []).filter(fi => {
                        if (fb.applied.feedbackFilter === 'helpful') return fi.isHelpful;
                        if (fb.applied.feedbackFilter === 'not-helpful') return !fi.isHelpful;
                        if (fb.applied.feedbackFilter === 'unreviewed') return !fi.isHelpful && !fi.reviewed;
                        return true;
                      });
                      return (
                        <div key={faq.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                          {/* FAQ header row — click to expand */}
                          <button
                            onClick={() => toggleFaqExpanded(faq.id)}
                            className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex flex-col lg:flex-row justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className="text-lg">{faq.sectionIcon}</span>
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                    {faq.sectionTitle}
                                  </span>
                                  {metrics.unreviewed > 0 && (
                                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                                      {metrics.unreviewed} unreviewed
                                    </span>
                                  )}
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                  </svg>
                                </div>
                                <h4 className="font-semibold text-gray-900 mb-1 break-words">{faq.question}</h4>
                                <p className="text-sm text-gray-600 line-clamp-2 break-words">{faq.answer}</p>
                              </div>
                              
                              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                <div className={`text-3xl font-bold ${
                                  metrics.satisfactionRate >= 75 ? 'text-green-600' :
                                  metrics.satisfactionRate >= 50 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {metrics.satisfactionRate}%
                                </div>
                                <div className="text-sm text-gray-600">
                                  {metrics.total} {metrics.total === 1 ? 'vote' : 'votes'}
                                </div>
                                <div className="flex gap-3 text-sm">
                                  <div className="flex items-center gap-1 text-green-600">
                                    <ThumbsUpIcon className="w-4 h-4" />
                                    <span>{metrics.helpful}</span>
                                  </div>
                                  <div className="flex items-center gap-1 text-red-600">
                                    <ThumbsDownIcon className="w-4 h-4" />
                                    <span>{metrics.notHelpful}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </button>

                          {/* Expanded: individual feedback entries */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
                              {visibleItems.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">
                                  No feedback matching the current filter.
                                </p>
                              ) : (
                                visibleItems
                                  .sort((a, b) => {
                                    const ta = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
                                    const tb = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
                                    return tb - ta;
                                  })
                                  .map(fb => (
                                    <div 
                                      key={fb.id}
                                      className={`border rounded-lg p-3 bg-white ${
                                        fb.isHelpful ? 'border-green-200' : 'border-red-200'
                                      }`}
                                    >
                                      <div className="flex flex-col sm:flex-row justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                          {/* Badges */}
                                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                                            {fb.isHelpful ? (
                                              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                                                👍 Helpful
                                              </span>
                                            ) : (
                                              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                                                👎 Not Helpful
                                              </span>
                                            )}
                                            {!fb.isHelpful && !fb.reviewed && (
                                              <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                                                Unreviewed
                                              </span>
                                            )}
                                            {fb.reviewed && (
                                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                                ✓ Reviewed
                                              </span>
                                            )}
                                          </div>

                                          {/* User comment */}
                                          {fb.comment && (
                                            <div className="mb-2">
                                              <div className="text-xs font-medium text-gray-500 mb-1">User Comment:</div>
                                              <p className="text-sm text-red-700 italic bg-red-50 rounded p-2">
                                                "{fb.comment}"
                                              </p>
                                            </div>
                                          )}

                                          {/* Admin notes */}
                                          {fb.adminNotes && (
                                            <div className="mb-2">
                                              <div className="text-xs font-medium text-gray-500 mb-1">Admin Notes:</div>
                                              <p className="text-sm text-indigo-700 bg-indigo-50 rounded p-2">
                                                {fb.adminNotes}
                                              </p>
                                            </div>
                                          )}

                                          {/* Meta */}
                                          <div className="text-xs text-gray-500 flex gap-4 flex-wrap">
                                            <span>By: {fb.userName}</span>
                                            <span>{fb.timestamp ? new Date(fb.timestamp).toLocaleString() : 'Unknown date'}</span>
                                          </div>
                                        </div>

                                        {/* Actions */}
                                        {!isReadOnly && (
                                          <div className="flex flex-col gap-2 flex-shrink-0">
                                            {!fb.isHelpful && !fb.reviewed && (
                                              <Tooltip text="Mark as reviewed">
                                                <button
                                                  onClick={() => dismissFeedback(fb.id)}
                                                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                                                >
                                                  Dismiss
                                                </button>
                                              </Tooltip>
                                            )}
                                            {!fb.isHelpful && (
                                              notingFeedbackId === fb.id ? (
                                                <div className="flex flex-col gap-2 w-56">
                                                  <textarea
                                                    value={feedbackNoteText}
                                                    onChange={(e) => setFeedbackNoteText(e.target.value)}
                                                    placeholder="What should be improved..."
                                                    className="text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 resize-none"
                                                    rows={3}
                                                    autoFocus
                                                  />
                                                  <div className="flex gap-2">
                                                    <button
                                                      onClick={() => handleSaveFeedbackNote(fb.id)}
                                                      disabled={savingFeedbackNote || !feedbackNoteText.trim()}
                                                      className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                                                    >
                                                      {savingFeedbackNote ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                      onClick={() => { setNotingFeedbackId(null); setFeedbackNoteText(''); }}
                                                      className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <Tooltip text={fb.adminNotes ? 'Edit admin notes' : 'Add notes about what should be improved'}>
                                                  <button
                                                    onClick={() => { setNotingFeedbackId(fb.id); setFeedbackNoteText(fb.adminNotes || ''); }}
                                                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                                                  >
                                                    {fb.adminNotes ? '✏️ Edit Notes' : '📝 Add Notes'}
                                                  </button>
                                                </Tooltip>
                                              )
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>

                {faqs.filter(faq => {
                  const metrics = faqMetrics[faq.id];
                  if (!metrics || metrics.total === 0) return false;
                  if (fb.applied.feedbackFilter === 'helpful') return metrics.helpful > 0;
                  if (fb.applied.feedbackFilter === 'not-helpful') return metrics.notHelpful > 0;
                  if (fb.applied.feedbackFilter === 'unreviewed') return metrics.unreviewed > 0;
                  return true;
                }).filter(faq => {
                  if (!fb.applied.feedbackSearch) return true;
                  const q = fb.applied.feedbackSearch.toLowerCase();
                  return (faq.question || '').toLowerCase().includes(q)
                    || (faq.answer || '').toLowerCase().includes(q)
                    || (faq.sectionTitle || '').toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No feedback data available yet.
                  </div>
                )}
              </div>
            )}

            {/* Lifecycle Management Tab */}
            {activeTab === 'lifecycle' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Content Lifecycle</h3>
                  <p className="text-sm text-gray-600">Monitor article freshness, review schedules, and content health</p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Drafts</div>
                    <div className="text-2xl font-bold text-gray-500">{lifecycleMetrics.drafts.length}</div>
                  </div>
                  <div className="bg-white border border-amber-200 rounded-lg p-4">
                    <div className="text-sm text-amber-700 mb-1">Needs Review</div>
                    <div className="text-2xl font-bold text-amber-600">{lifecycleMetrics.needsReview.length}</div>
                  </div>
                  <div className="bg-white border border-orange-200 rounded-lg p-4">
                    <div className="text-sm text-orange-700 mb-1">Review Overdue</div>
                    <div className="text-2xl font-bold text-orange-600">{lifecycleMetrics.reviewOverdue.length}</div>
                  </div>
                  <div className="bg-white border border-red-200 rounded-lg p-4">
                    <div className="text-sm text-red-700 mb-1">Expired</div>
                    <div className="text-2xl font-bold text-red-600">{lifecycleMetrics.expired.length}</div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-1">Archived</div>
                    <div className="text-2xl font-bold text-gray-500">{lifecycleMetrics.archived.length}</div>
                  </div>
                  <div className="bg-white border border-indigo-200 rounded-lg p-4 col-span-2 sm:col-span-1">
                    <div className="text-sm text-indigo-700 mb-1">Missing Review Date</div>
                    <div className="text-2xl font-bold text-indigo-600">{lifecycleMetrics.missingReviewDate.length}</div>
                  </div>
                </div>

                {/* Items Needing Attention */}
                {(lifecycleMetrics.reviewOverdue.length > 0 || lifecycleMetrics.expired.length > 0 || lifecycleMetrics.needsReview.length > 0) && (
                  <div className="bg-white border border-orange-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-orange-50 border-b border-orange-200">
                      <h4 className="font-semibold text-orange-800">Items Needing Attention</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {[...lifecycleMetrics.expired, ...lifecycleMetrics.reviewOverdue, ...lifecycleMetrics.needsReview]
                        .filter((item, idx, arr) => arr.findIndex(i => i.id === item.id) === idx)
                        .map(item => {
                          const today = new Date().toISOString().split('T')[0];
                          const isExpired = item.expiresAt && item.expiresAt <= today;
                          const isOverdue = item.reviewDate && item.reviewDate <= today;
                          return (
                            <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-gray-50">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`text-xs px-2 py-0.5 rounded ${item.itemType === 'faq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {item.itemType === 'faq' ? 'FAQ' : 'KB'}
                                  </span>
                                  {isExpired && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">Expired {item.expiresAt}</span>
                                  )}
                                  {isOverdue && !isExpired && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">Review due {item.reviewDate}</span>
                                  )}
                                  {item.status === 'needs-review' && !isOverdue && !isExpired && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Flagged for review</span>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-gray-900 mt-1 truncate">{item.title}</p>
                                <p className="text-xs text-gray-500">{item.category || item.sectionTitle || 'general'}</p>
                              </div>
                              {!isReadOnly && (
                                <button
                                  onClick={() => {
                                    if (item.itemType === 'faq') {
                                      setEditingFaq(item);
                                      setShowFaqEditor(true);
                                    } else {
                                      setEditingEntry(item);
                                      setShowEditModal(true);
                                    }
                                  }}
                                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                                >
                                  Review
                                </button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {lifecycleMetrics.reviewOverdue.length === 0 && lifecycleMetrics.expired.length === 0 && lifecycleMetrics.needsReview.length === 0 && lifecycleMetrics.missingReviewDate.length === 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <p className="text-green-800 font-medium">All content is up to date</p>
                    <p className="text-green-600 text-sm mt-1">No articles require review or have expired.</p>
                  </div>
                )}

                {/* Missing Review Date Section */}
                {lifecycleMetrics.missingReviewDate.length > 0 && (
                  <div className="bg-white border border-indigo-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200">
                      <h4 className="font-semibold text-indigo-800">Missing Review Date ({lifecycleMetrics.missingReviewDate.length})</h4>
                      <p className="text-xs text-indigo-600 mt-0.5">These published or needs-review items have no scheduled review date.</p>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {lifecycleMetrics.missingReviewDate.map(item => (
                        <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${item.itemType === 'faq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                {item.itemType === 'faq' ? 'FAQ' : 'KB'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                item.status === 'needs-review' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {item.status === 'needs-review' ? 'Needs Review' : 'Published'}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mt-1 truncate">{item.title}</p>
                            <p className="text-xs text-gray-500">
                              Updated {item.updatedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                            </p>
                          </div>
                          {!isReadOnly && (
                            <button
                              onClick={() => {
                                if (item.itemType === 'faq') {
                                  setEditingFaq(item);
                                  setShowFaqEditor(true);
                                } else {
                                  setEditingEntry(item);
                                  setShowEditModal(true);
                                }
                              }}
                              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0"
                            >
                              Set Date
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Drafts Section */}
                {lifecycleMetrics.drafts.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <h4 className="font-semibold text-gray-800">Draft Articles ({lifecycleMetrics.drafts.length})</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {lifecycleMetrics.drafts.map(item => (
                        <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${item.itemType === 'faq' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                {item.itemType === 'faq' ? 'FAQ' : 'KB'}
                              </span>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mt-1 truncate">{item.title}</p>
                            <p className="text-xs text-gray-500">
                              Created {item.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                              {item.createdByName && ` by ${item.createdByName}`}
                            </p>
                          </div>
                          {!isReadOnly && (
                            <button
                              onClick={() => {
                                if (item.itemType === 'faq') {
                                  setEditingFaq(item);
                                  setShowFaqEditor(true);
                                } else {
                                  setEditingEntry(item);
                                  setShowEditModal(true);
                                }
                              }}
                              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </>
        )}

        {/* Toast Notification */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* FAQ Editor Modal */}
        {showFaqEditor && (
          <SupportPageFaqEditor
            initialFaq={editingFaq}
            sections={sections}
            onClose={() => {
              setShowFaqEditor(false);
              setEditingFaq(null);
            }}
            onSaved={(message) => {
              setToast({ message, type: 'success' });
              setShowFaqEditor(false);
              setEditingFaq(null);
            }}
          />
        )}

        {/* Section Editor Modal */}
        {showSectionEditor && (
          <SupportPageSectionEditor
            initialSection={editingSection}
            onClose={() => {
              setShowSectionEditor(false);
              setEditingSection(null);
            }}
            onSaved={(message) => {
              setToast({ message, type: 'success' });
              setShowSectionEditor(false);
              setEditingSection(null);
            }}
          />
        )}

        {/* Knowledge Base Article Editor Modal (create or edit) */}
        {showEditModal && (
          <KnowledgeBaseArticleEditor
            article={editingEntry}
            categories={categories}
            onClose={() => {
              setShowEditModal(false);
              setEditingEntry(null);
            }}
            onSaved={(message) => {
              setToast({ message, type: 'success' });
              setShowEditModal(false);
              setEditingEntry(null);
              reloadKnowledgeBase?.();
            }}
          />
        )}

        {/* Promote to KB Modal */}
        {promotingFeedback && (
          <PromoteToKBModal
            feedback={promotingFeedback}
            categories={categories}
            onClose={() => setPromotingFeedback(null)}
            onPromote={isReadOnly ? null : promoteToKnowledgeBase}
          />
        )}
      </div>
    </main>
  );
};

export default KnowledgeManagementPage;
